/**
 * Link Admin — `_auth` pathless authenticated layout.
 *
 * Wraps every authenticated route in `<AppShell aud="platform">` per Phase 2
 * Plan 02-12. Platform-specific diffs from HR Admin:
 *
 *   - Platform audience skips tenant-branding; UserMenu hides the profile
 *     link (D-13); BillingBanner never renders (middleware skips
 *     /_platform/* URLs, role gate rejects non-HR_ADMIN).
 *   - The profile-link prop is intentionally omitted (platform audience hides
 *     the profile menu item anyway).
 *   - Preferences-change callback is NOT passed — AppShell's
 *     usePrefsReconcile handles the PATCH internally via `@linkhr/api-client`.
 *     NO raw network-call escape hatch at the app layer (FOUND-03 +
 *     project conventions §"Data fetching").
 *
 * Shell chrome (DesktopOnlyScrim <1024px, 3-group sidebar, topbar, user menu)
 * is wired by `<AppShell>`. Route body (`<Outlet/>`) renders child route
 * content — overview, tenants, billing, and audit-log are live (the former
 * ComingSoon surfaces were removed from `nav.ts`; they return with routes).
 */
import type { JSX } from "react";
import { Outlet, createFileRoute, useNavigate, useLocation } from "@tanstack/react-router";
import { useTranslation } from "@linkhr/i18n";
import { useQueryClient } from "@tanstack/react-query";
import { AppShell } from "@linkhr/design-system";
import { requireAuth, performLogout } from "@linkhr/auth";
import { useAuth } from "@linkhr/auth/react";
import { linkAdminNav } from "../nav";
import { linkAdminSearchableScreens } from "../registries/searchableScreens";
import { useTenantSearchSections } from "../registries/useTenantSearchSections";
import { env } from "../env";

export const Route = createFileRoute("/_auth")({
  beforeLoad: requireAuth,
  component: LinkAdminAuthLayout,
});

function LinkAdminAuthLayout(): JSX.Element {
  const { user, roles } = useAuth();
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const locale: "en" | "ar" = i18n.language.startsWith("ar") ? "ar" : "en";

  // Static screen registry + SERVER-side tenant search: the palette input
  // debounces into GET /_platform/tenants?q=… so a tenant is findable by
  // name (both languages) or slug at any tenant count.
  const { sections: searchableScreens, onSearchValueChange } = useTenantSearchSections(
    linkAdminSearchableScreens,
    locale,
  );

  // SPA-nav intercept — keeps the palette and (future) sidebar from full-
  // page-navigating, which would re-bootstrap auth on every click.
  const onShellNavigate = (href: string): void => {
    void navigate({ to: href });
  };

  const onLogout = async (): Promise<void> => {
    // performLogout ends with window.location.assign(loginPath) — hard-nav
    // resets React state so no local snapshot commit is needed.
    await performLogout({ queryClient, apiBaseUrl: env.VITE_API_BASE_URL });
  };

  const onLocaleChange = (next: "en" | "ar"): void => {
    void i18n.changeLanguage(next);
  };

  return (
    <AppShell
      nav={linkAdminNav}
      aud="platform"
      appName="Link Admin — Link HR"
      user={{
        displayName: user?.displayName ?? "",
        email: user?.email ?? null,
        roles,
        // Plan 08.1-02 — Link Admin keeps role-based gating per the
        // LINK_STAFF carve-out (08.1-01-SUMMARY). Pass empty Set so
        // filterNav falls through to the `roles?: ["LINK_STAFF"]` branch.
        permissions: new Set<string>(),
        avatarUrl: null,
      }}
      tenantName={null}
      onLogout={onLogout}
      locale={locale}
      onLocaleChange={onLocaleChange}
      onNavigate={onShellNavigate}
      searchableScreens={searchableScreens}
      onSearchValueChange={onSearchValueChange}
      activeHref={pathname}
    >
      <Outlet />
    </AppShell>
  );
}
